import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent } from "../ui/card";
import { PriorityBadge, STATUS_LABELS, STATUS_ORDER } from "./ticketBadges";
import type { Ticket } from "../../services/ticketsService";

interface TicketKanbanProps {
  tickets: Ticket[];
  showReporter?: boolean;
  onSelect: (ticketId: string) => void;
}

function KanbanCard({
  ticket,
  showReporter,
  onClick,
}: {
  ticket: Ticket;
  showReporter?: boolean;
  onClick: () => void;
}) {
  return (
    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={onClick}>
      <CardContent className="py-3 px-3 space-y-1.5">
        <div className="font-medium text-sm leading-snug">{ticket.title}</div>
        {ticket.module && (
          <div className="text-xs text-muted-foreground truncate">
            {ticket.module}
            {ticket.section && <> · {ticket.section}</>}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <PriorityBadge priority={ticket.priority} />
          <span className="text-xs text-muted-foreground truncate">
            {showReporter ? ticket.reporter_name || ticket.reporter_email : formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true, locale: es })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Solo lectura: agrupa por estado para ver de un vistazo cómo va todo, pero
// no hay drag-and-drop -- el cliente no puede cambiar el estado de un
// ticket (eso lo hace el equipo de desarrollo desde su propia app).
export function TicketKanban({ tickets, showReporter, onSelect }: TicketKanbanProps) {
  const porEstado = STATUS_ORDER.map((status) => ({
    status,
    tickets: tickets.filter((t) => t.status === status),
  })).filter((columna) => columna.tickets.length > 0 || ["abierto", "en_progreso", "en_revision"].includes(columna.status));

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {porEstado.map(({ status, tickets: columnTickets }) => (
        <div key={status} className="w-64 shrink-0">
          <div className="flex items-center justify-between px-1 mb-2">
            <span className="text-sm font-medium">{STATUS_LABELS[status]}</span>
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
              {columnTickets.length}
            </span>
          </div>
          <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
            {columnTickets.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-md">
                Sin tickets
              </div>
            ) : (
              columnTickets.map((ticket) => (
                <KanbanCard
                  key={ticket.id}
                  ticket={ticket}
                  showReporter={showReporter}
                  onClick={() => onSelect(ticket.id)}
                />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
